import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useEmployeeApprover } from "../../contexts/EmployeeApproverContext";
import Modal from "../../components/UI/Modal";
import EmployeeApproverForm from "../../components/Form/EmployeeApproverForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import Skeleton from "react-loading-skeleton";
import { MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid, Search, X } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import ReactPaginate from 'react-paginate';
import StatusBadge from '../../components/UI/StatusBadge';


// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteEmployeeApprover }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteEmployeeApprover(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const EmployeeApproverMaster = React.memo(() => {
  const { employeeApprovers, fetchEmployeeApprovers, addEmployeeApprover, updateEmployeeApprover, deleteEmployeeApprover, isLoading, pagination } = useEmployeeApprover();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployeeApprover, setEditingEmployeeApprover] = useState(null);
  const [message, setMessage] = useState(""); 
  const [formErrors, setFormErrors] = useState({}); 
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setIsClient(true); 
  }, []);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingEmployeeApprover(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((employeeApprover) => {
    setEditingEmployeeApprover(employeeApprover);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingEmployeeApprover(null);
    setFormErrors({});
    setMessage("");
  }, []);


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchEmployeeApprovers(newPage);
    }
  };

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (employeeApprover) => {
      setLoading(true);
      try {
        if (editingEmployeeApprover) {
          const employeeApproverData = employeeApprover instanceof FormData ? convertFormDataToObject(employeeApprover) : employeeApprover;
          await updateEmployeeApprover({ ...editingEmployeeApprover, ...employeeApproverData });
          setNotification({
            message: "Employee Approver updated successfully.",
            type: "success",
          });
        } else {
          await addEmployeeApprover(employeeApprover);
          setNotification({
            message: "Employee Approver added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        const apiErrors = err.response?.data?.errors || {};
        const apiMessage = err.response?.data?.message || err.response?.data?.error || "An error occurred";
        
        if (Object.keys(apiErrors).length > 0) {
          setFormErrors(apiErrors);
        } else {
          setNotification({
            message: apiMessage,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingEmployeeApprover, addEmployeeApprover, updateEmployeeApprover, closeModal]
  );

  // Memoize candidates with campaign names for the table
  const employeeApproverModuleForTable = useMemo(
    () =>
      employeeApprovers.map((employeeApprover) => ({
        ...employeeApprover,
        name: employeeApprover?.name || "Unknown Employee Approver",
      })),
    [employeeApprovers]
  );

  const filteredEmployeeApprovers = useMemo(() => {
    return employeeApproverModuleForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      // Searching by EMP ID, Primary Approver ID, or Secondary Approver ID (as strings)
      const matchesSearch = 
        String(item.employee_id || "").toLowerCase().includes(searchTerm) ||
        String(item.employee?.employee_id || "").toLowerCase().includes(searchTerm) ||
        String(item.primary_approver_id || "").toLowerCase().includes(searchTerm) ||
        String(item.secondary_approver_id || "").toLowerCase().includes(searchTerm);
      
      const matchesStatus = 
        statusFilter === "" || 
        String(item.is_active) === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [employeeApproverModuleForTable, searchQuery, statusFilter]);

  const handleDeleteEmployeeApprover = async (id) => {
    try {
      await deleteEmployeeApprover(id);
      // setNotification("Employee Approver deleted successfully");
      setNotification({
        message: "Employee Approver deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete employee approver");
      setNotification({
        message: "Failed to delete employee approver",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Employee", field: "employee_id", 
        render: (row) => (
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{row.employee?.full_name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{row.employee?.employee_id}</p>
          </div>
        )
      },
      { label: "Module", field: "approval_module_id",
        render: (row) => row.module?.display_name || row.approval_module_id
      },
      { label: "Level", field: "approval_level_id",
        render: (row) => (
          <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded text-xs font-medium">
            {row.level?.name || row.approval_level_id}
          </span>
        )
      },
      { label: "Primary Approver", field: "primary_approver_id",
        render: (row) => row.primary_user?.name || row.primary_approver_id
      },
      { label: "Secondary Approver", field: "secondary_approver_id",
        render: (row) => row.secondary_user?.name || row.secondary_approver_id || "N/A"
      },
      { label: "Status", field: "is_active",
        render: (row) => (
          <StatusBadge status={(row?.is_active === 1 || row?.is_active === "1") ? "active" : "inactive"} />
        ),
       },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteEmployeeApprover={handleDeleteEmployeeApprover}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteEmployeeApprover,
    ]
  );

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Employee Approver Master</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by EMP ID or Approver ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Filter and Add Button */}
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="w-full sm:w-48 relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none cursor-pointer font-poppins"
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 text-white px-4 py-2 font-medium rounded-md hover:bg-blue-700 whitespace-nowrap"
          >
            Add Employee Approver
          </button>
        </div>
      </div>

        {/* Success Message */}
        {message && (
            <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
            {message}
            </div>
        )}

        {/* Skeleton Loader */}
        {loading ? (
            <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} height={40} />
            ))}
            </div>
        ) : (
            <div>
            <ReusableTable
                data={filteredEmployeeApprovers}
                // data={requests.slice().reverse()}
                loading={isLoading}
                columns={columns}
                filterable={false}
                excludeFilterColumns={["actions"]}
                // excludeFilterColumns={excludeFilterColumns}
            />
            </div>
        )}

        {/* Pagination */}
        {!isFiltering && employeeApprovers?.length > 0 && (
          // <ReactPaginate
          //     breakLabel="..."
          //     onPageChange={(event) => handlePageChange(event.selected + 1)}
          //     forcePage={currentPage - 1}
          //     pageRangeDisplayed={5}
          //     pageCount={pagination.totalPages}
          //     renderOnZeroPageCount={null}
          //     nextLabel="Next >"
          //     previousLabel="< Prev"
          //     previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
          //     nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          //     containerClassName="flex items-center justify-center mt-4"
          //     pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
          //     activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
          // />

          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
            previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}


      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingEmployeeApprover ? "Edit Employee Approver" : "Add Employee Approver"}
      >
        <EmployeeApproverForm
          loading={loading} 
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingEmployeeApprover || { employee_id: "", module_id: "", level_id: "", stage: "", primary_approver_id: "", secondary_approver_id: "", effective_from: "", effective_to: "", is_active: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default EmployeeApproverMaster;
